import { useState, type FormEvent } from 'react';
import { Button, Field } from '@buildmate/ui-web';
import { AuthError, newId } from '@buildmate/core';
import { usePorts } from '../lib/ports-context.tsx';

type Mode = 'signIn' | 'signUp';

/**
 * The invite token this form is standing in for, if any.
 *
 * Read from the address bar rather than from router state, because the
 * only thing that has to survive is a string in a URL somebody may well
 * have pasted — and `AcceptInvite` navigates with `location.assign`, so
 * the two halves agree about where it lives.
 */
function inviteToken(): string | null {
  try {
    return new URLSearchParams(globalThis.location?.search ?? '').get('invite');
  } catch {
    return null;
  }
}

/**
 * Sign in, create a company, or **create an account to accept an invite**.
 *
 * The third was missing, and it made the invite flow a dead end: the only
 * account-creating path demanded a company name and called
 * `bootstrapCompany`, so somebody invited to an existing company could
 * either sign in with an account they did not have, or create a company of
 * their own — which is not what the link offered them. Reported from
 * hand-testing on dev.
 *
 * With `?invite=<token>` present the company-name field is gone, nothing is
 * bootstrapped, and both paths end at `/invite/<token>` so the existing
 * redemption runs. One place redeems an invite, and it is not this file.
 */
export function SignIn() {
  const ports = usePorts();
  const [invite] = useState(inviteToken);
  const [mode, setMode] = useState<Mode>('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Held for the life of the form, not regenerated per attempt, so a retry
  // after a failure is recognised as the same request.
  const [companyId] = useState(() => newId());

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'signIn') {
        await ports.auth.signInWithEmail(email, password);
        // Back to the invite, which accepts it. Without this the token was
        // simply lost and the page had to tell somebody to go and find the
        // link again.
        if (invite) {
          globalThis.location?.assign(`/invite/${invite}`);
          return;
        }
      } else if (invite) {
        // An account and nothing else. The company is the invite's to give,
        // and `acceptInvite` is what writes the membership.
        await ports.auth.signUpWithEmail(email, password, name);
        globalThis.location?.assign(`/invite/${invite}`);
        return;
      } else {
        await ports.auth.signUpWithEmail(email, password, name);
        // The account exists but belongs to no company until this returns.
        // It runs on the server so the company, its roles and the owner's
        // membership are created together or not at all.
        //
        // The id is generated here, once, and reused if this is retried: the
        // same id is the same company, so a double submit or a dropped
        // response converges on one record instead of creating two.
        await ports.functions.call('bootstrapCompany', { companyId, companyName });
        // Re-read so the new membership is picked up without a reload.
        await ports.auth.listMemberships();
        globalThis.location?.reload();
      }
    } catch (cause) {
      setError(
        cause instanceof AuthError
          ? cause.message
          : mode === 'signUp'
            ? invite
              ? 'Could not create the account. Please try again.'
              : 'Could not create the company. Please try again.'
            : 'Could not sign in. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  const isSignUp = mode === 'signUp';

  return (
    <main className="flex min-h-full items-center justify-center p-4">
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Buildmate</h1>
        <p className="mt-1 text-sm text-slate-600">
          {invite
            ? isSignUp
              ? 'Create an account to accept your invite.'
              : 'Sign in to accept your invite.'
            : isSignUp
              ? 'Create your company.'
              : 'Sign in to your company.'}
        </p>

        {/* Said before they type it, not after the server refuses. An
            invite is bound to the address it was sent to, so an account
            made with a different one cannot redeem it — and the person
            would be left holding an account that belongs to nothing. */}
        {invite && (
          <p className="mt-2 rounded-md bg-slate-100 px-3 py-2 text-xs text-slate-600">
            Use the email address the invite was sent to. An invite only works for that address.
          </p>
        )}

        <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
          {isSignUp && (
            <>
              <Field
                label="Your name"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
              {/* Absent on an invite: the company already exists, and
                  asking for its name would invite somebody to create a
                  second one beside it. */}
              {!invite && (
                <Field
                  label="Company name"
                  autoComplete="organization"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                />
              )}
            </>
          )}
          <Field
            label="Email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <Field
            label="Password"
            type="password"
            autoComplete={isSignUp ? 'new-password' : 'current-password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={error ?? undefined}
            required
          />
          <Button type="submit" loading={busy}>
            {isSignUp ? (invite ? 'Create account' : 'Create company') : 'Sign in'}
          </Button>
        </form>

        <p className="mt-4 text-sm text-slate-600">
          {isSignUp
            ? 'Already have an account?'
            : invite
              ? 'No account yet?'
              : 'Setting up a new company?'}{' '}
          <button
            type="button"
            className="font-medium text-blue-600 underline-offset-2 hover:underline"
            onClick={() => {
              setMode(isSignUp ? 'signIn' : 'signUp');
              setError(null);
            }}
          >
            {isSignUp ? 'Sign in' : invite ? 'Create an account' : 'Create one'}
          </button>
        </p>
      </div>
    </main>
  );
}
